export interface GenericResponse {
  /**
   * Http Status code of the request
   */
  status: number;
  /**
   * This is a human-readable message indicating what happened on the call. However, these messages are often not
   * fit for our clients. Do not display this message to users unless specifically called for in the API call
   */
  message: string;
  /**
   * This contains the successful response object
   */
  data: any;
  /**
   * An array of any errors that happened in the call. Currently only populated on failed API calls
   */
  errors: string[];
}

/**
 * Shape of the data returned from the backend
 *
 * Data has the successful response, errors has any error messages, message has a generic message string
 */
export interface ApiResponse<T> extends GenericResponse {
  /**
   * This contains the successful response object
   */
  data: T;
}

/**
 * True when a response is the bare envelope rather than a payload.
 *
 * For backwards compatibility the api falls back to returning the whole response when it carries no
 * `data` — fetching a draft version that doesn't exist, for example. Callers get `status`, `message`
 * and `errors` typed as whatever they asked for, so this guard keeps that out of state.
 */
export const isGenericResponse = (response: unknown): response is GenericResponse =>
  typeof response === 'object' &&
  response !== null &&
  typeof (response as GenericResponse).status === 'number' &&
  Array.isArray((response as GenericResponse).errors);
